// src/components/HeaderReminders.js
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    IconButton,
    Badge,
    Popover,
    Typography,
    Button,
    Chip,
    CircularProgress,
    Divider,
    Tooltip,
} from '@mui/material';
import AlarmIcon from '@mui/icons-material/Alarm';
import SnoozeIcon from '@mui/icons-material/Snooze';
import NotInterestedIcon from '@mui/icons-material/NotInterested';
import axiosInstance from '../utils/axiosInstance';
import { usePanel, PanelType } from '../contexts/PanelContext';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";

const PRIORITY_CONFIG = {
    high: { color: '#ef4444', bg: '#fee2e2', label: 'High' },
    medium: { color: '#f59e0b', bg: '#fef3c7', label: 'Medium' },
    low: { color: '#3b82f6', bg: '#dbeafe', label: 'Low' },
};

function timeAgo(dateStr) {
    if (!dateStr) return '';
    const target = new Date(dateStr).getTime();
    const now = Date.now();
    const diff = target - now;

    if (diff < 0) {
        const mins = Math.floor(Math.abs(diff) / 60000);
        if (mins < 60) return `${mins}m overdue`;
        const hrs = Math.floor(mins / 60);
        if (hrs < 24) return `${hrs}h overdue`;
        return `${Math.floor(hrs / 24)}d overdue`;
    }
    const mins = Math.floor(diff / 60000);
    if (mins < 60) return `in ${mins}m`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `in ${hrs}h`;
    return `in ${Math.floor(hrs / 24)}d`;
}

export default function HeaderReminders({ panelColor }) {
    const [anchorEl, setAnchorEl] = useState(null);
    const [reminders, setReminders] = useState([]);
    const [activeCount, setActiveCount] = useState(0);
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();
    const { activePanel } = usePanel();

    const getPanelType = () => {
        switch (activePanel) {
            case PanelType.STUDENT: return 'student';
            case PanelType.CUSTOMER: return 'customer';
            default: return 'creator';
        }
    };

    const getViewAllPath = () => {
        switch (activePanel) {
            case PanelType.STUDENT:
                return '/student-portal/reminders';
            case PanelType.CUSTOMER:
                return '/customer-panel/reminders';
            default:
                return '/creator-panel/reminders';
        }
    };

    const fetchReminders = useCallback(async () => {
        try {
            setLoading(true);
            const res = await axiosInstance.get('/reminders', {
                params: { view: 'mine', status: 'ACTIVE', limit: 5, page: 1, panel_type: getPanelType() },
            });
            const data = res.data;
            const items = data.data || data.reminders || [];
            setReminders(items);
            setActiveCount(data.total || data.totalCount || items.length);
        } catch {
            // silent
        } finally {
            setLoading(false);
        }
    }, [activePanel]);

    useEffect(() => {
        fetchReminders();
        const interval = setInterval(fetchReminders, 60000);
        return () => clearInterval(interval);
    }, [fetchReminders]);

    const handleOpen = (e) => {
        setAnchorEl(e.currentTarget);
        fetchReminders();
    };

    const handleClose = () => setAnchorEl(null);

    const handleSnooze = async (reminder) => {
        const id = reminder.id || reminder._id;
        try {
            await axiosInstance.put(`/reminders/${id}/status`, {
                action: 'snooze',
                duration_minutes: 60,
            });
            fetchReminders();
        } catch {
            // silent
        }
    };

    const handleDismiss = async (reminder) => {
        const id = reminder.id || reminder._id;
        try {
            await axiosInstance.put(`/reminders/${id}/status`, { action: 'dismiss' });
            fetchReminders();
        } catch {
            // silent
        }
    };

    const handleViewAll = () => {
        handleClose();
        navigate(getViewAllPath());
    };

    const open = Boolean(anchorEl);

    return (
        <Box sx={{ mr: { xs: 0, md: 0.5 } }}>
            <Tooltip title="Reminders">
                <IconButton
                    onClick={handleOpen}
                    sx={{
                        width: 38,
                        height: 38,
                        borderRadius: '10px',
                        bgcolor: `${panelColor}14`,
                        border: `1px solid ${panelColor}26`,
                        '&:hover': { bgcolor: `${panelColor}1f` },
                    }}
                >
                    <Badge
                        badgeContent={activeCount}
                        max={99}
                        sx={{
                            '& .MuiBadge-badge': {
                                bgcolor: '#ef4444',
                                color: '#fff',
                                fontSize: '0.55rem',
                                fontWeight: 700,
                                minWidth: 16,
                                height: 16,
                                top: -2,
                                right: -2,
                            },
                        }}
                    >
                        <AlarmIcon sx={{ fontSize: '1.2rem', color: panelColor }} />
                    </Badge>
                </IconButton>
            </Tooltip>

            <Popover
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                marginThreshold={8}
                PaperProps={{
                    sx: {
                        width: { xs: 'calc(100vw - 16px)', sm: 360 },
                        maxWidth: { xs: 320, sm: 360 },
                        maxHeight: { xs: '70vh', sm: 480 },
                        borderRadius: { xs: '10px', sm: '14px' },
                        boxShadow: '0 12px 40px rgba(0,0,0,0.15)',
                        border: '1px solid rgba(0,0,0,0.08)',
                        mt: 1,
                        display: 'flex',
                        flexDirection: 'column',
                    },
                }}
            >
                {/* Header */}
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: { xs: 1.5, sm: 2 }, py: { xs: 1, sm: 1.5 }, borderBottom: '1px solid rgba(0,0,0,0.06)', flexShrink: 0 }}>
                    <Box>
                        <Typography sx={{ fontFamily, fontSize: { xs: '0.8rem', sm: '0.9rem' }, fontWeight: 700, color: '#1e293b' }}>
                            Reminders
                        </Typography>
                        {activeCount > 0 && (
                            <Typography sx={{ fontFamily, fontSize: '0.7rem', color: panelColor, fontWeight: 600 }}>
                                {activeCount} active
                            </Typography>
                        )}
                    </Box>
                </Box>

                {/* List */}
                <Box sx={{
                    overflowY: 'auto',
                    flex: 1,
                    '&::-webkit-scrollbar': { width: '3px' },
                    '&::-webkit-scrollbar-thumb': { background: 'rgba(0,0,0,0.15)', borderRadius: '4px' },
                }}>
                    {loading && reminders.length === 0 ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: { xs: 3, sm: 4 } }}>
                            <CircularProgress size={24} sx={{ color: panelColor }} />
                        </Box>
                    ) : reminders.length === 0 ? (
                        <Box sx={{ textAlign: 'center', py: { xs: 3, sm: 4 }, px: 2 }}>
                            <AlarmIcon sx={{ fontSize: { xs: 30, sm: 40 }, color: '#e2e8f0', mb: 1 }} />
                            <Typography sx={{ fontFamily, fontSize: { xs: '0.8rem', sm: '0.85rem' }, color: '#94a3b8' }}>
                                No active reminders
                            </Typography>
                        </Box>
                    ) : (
                        reminders.map((r, idx) => {
                            const priority = (r.priority || 'medium').toLowerCase();
                            const pConfig = PRIORITY_CONFIG[priority] || PRIORITY_CONFIG.medium;
                            return (
                                <Box key={r.id || r._id || idx}>
                                    <Box sx={{ px: { xs: 1.5, sm: 2 }, py: { xs: 1, sm: 1.5 } }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                                            <Typography sx={{
                                                fontFamily,
                                                fontSize: { xs: '0.8rem', sm: '0.85rem' },
                                                fontWeight: 600,
                                                color: '#1e293b',
                                                flex: 1,
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap',
                                                mr: 1,
                                            }}>
                                                {r.title}
                                            </Typography>
                                            <Chip
                                                label={pConfig.label}
                                                size="small"
                                                sx={{
                                                    fontFamily,
                                                    fontSize: { xs: '0.6rem', sm: '0.65rem' },
                                                    fontWeight: 600,
                                                    height: { xs: 18, sm: 20 },
                                                    bgcolor: pConfig.bg,
                                                    color: pConfig.color,
                                                }}
                                            />
                                        </Box>
                                        <Typography sx={{ fontFamily, fontSize: { xs: '0.65rem', sm: '0.7rem' }, color: '#94a3b8' }}>
                                            {timeAgo(r.remind_at)}
                                            {r.creator_name && ` · From ${r.creator_name}`}
                                        </Typography>
                                        <Box sx={{ display: 'flex', gap: 0.5, mt: { xs: 0.75, sm: 1 } }}>
                                            <Tooltip title="Snooze 1 hour">
                                                <IconButton
                                                    size="small"
                                                    onClick={() => handleSnooze(r)}
                                                    sx={{ color: '#8b5cf6', bgcolor: '#ede9fe', '&:hover': { bgcolor: '#ddd6fe' }, width: { xs: 24, sm: 28 }, height: { xs: 24, sm: 28 } }}
                                                >
                                                    <SnoozeIcon sx={{ fontSize: { xs: 14, sm: 16 } }} />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Dismiss">
                                                <IconButton
                                                    size="small"
                                                    onClick={() => handleDismiss(r)}
                                                    sx={{ color: '#ef4444', bgcolor: '#fee2e2', '&:hover': { bgcolor: '#fecaca' }, width: { xs: 24, sm: 28 }, height: { xs: 24, sm: 28 } }}
                                                >
                                                    <NotInterestedIcon sx={{ fontSize: { xs: 14, sm: 16 } }} />
                                                </IconButton>
                                            </Tooltip>
                                        </Box>
                                    </Box>
                                    {idx < reminders.length - 1 && <Divider />}
                                </Box>
                            );
                        })
                    )}
                </Box>

                {/* Footer */}
                <Divider />
                <Box sx={{ p: { xs: 1, sm: 1.5 }, textAlign: 'center', flexShrink: 0 }}>
                    <Button
                        onClick={handleViewAll}
                        sx={{
                            fontFamily,
                            fontSize: { xs: '0.75rem', sm: '0.8rem' },
                            fontWeight: 600,
                            color: panelColor,
                            textTransform: 'none',
                            '&:hover': { bgcolor: `${panelColor}14` },
                        }}
                    >
                        View All Reminders
                    </Button>
                </Box>
            </Popover>
        </Box>
    );
}
